/*
 * XMYPICK Niche Storefront Engine — client niche context (spec §12.4).
 *
 * The server is authoritative. Every product, collection, page, article and blog
 * template resolves its niche in Liquid and stamps `data-xn-niche` on <html> before
 * first paint, so this file NEVER applies the niche identity for those pages and can
 * therefore be deferred without causing a flash of parent branding.
 *
 * This file only covers what the server cannot resolve:
 *   1. a valid `xn_niche` URL parameter (Liquid cannot read query parameters)
 *   2. the last valid niche stored for this session
 *   3. parent XMYPICK fallback
 *
 * It is never used to classify cart line items (spec §18.1) and it never sends niche
 * data anywhere — sessionStorage only, same origin, no network calls.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'xn_niche';
  var root = document.documentElement;

  function allowedKeys() {
    var raw = root.getAttribute('data-xn-niches') || '';
    return raw
      .split(',')
      .map(function (key) {
        return key.trim().toLowerCase();
      })
      .filter(Boolean);
  }

  function isValid(key) {
    if (!key) return false;
    return allowedKeys().indexOf(String(key).trim().toLowerCase()) !== -1;
  }

  function read() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function write(key) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, key);
    } catch (error) {
      /* Private mode or storage disabled — context simply does not persist. */
    }
  }

  function fromUrl() {
    try {
      return new URL(window.location.href).searchParams.get(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function serverKey() {
    var key = root.getAttribute('data-xn-niche');
    return isValid(key) ? key.trim().toLowerCase() : '';
  }

  /*
   * Applying a niche on the client is only ever done for a page the server could not
   * resolve. It is limited to the data attribute and the landing-page link, so no
   * layout shift is possible: no element is inserted, removed or resized.
   */
  function applyClientNiche(key) {
    if (root.getAttribute('data-xn-niche') === key) return;
    root.setAttribute('data-xn-niche', key);
    root.setAttribute('data-xn-niche-source', 'client');
  }

  function updateContinueLinks(key) {
    var links = document.querySelectorAll('[data-xn-continue]');
    if (!links.length) return;

    Array.prototype.forEach.call(links, function (link) {
      var target = link.getAttribute('data-xn-continue-' + key);
      if (target) {
        link.setAttribute('href', target);
        var label = link.getAttribute('data-xn-continue-label-' + key);
        if (label) link.textContent = label;
        link.hidden = false;
      }
    });
  }

  function init() {
    var resolved = serverKey();

    if (!resolved) {
      var urlKey = fromUrl();
      if (isValid(urlKey)) {
        resolved = urlKey.trim().toLowerCase();
        applyClientNiche(resolved);
      } else {
        var stored = read();
        if (isValid(stored)) {
          resolved = stored.trim().toLowerCase();
          /*
           * A stored key is deliberately NOT painted onto pages that legitimately
           * belong to the parent (portal, policies, account, checkout, errors).
           * It is only used to point "continue shopping" at the right store.
           */
        }
      }
    }

    if (resolved) {
      write(resolved);
      updateContinueLinks(resolved);
      window.XNNiche = { key: resolved, source: serverKey() ? 'server' : 'client' };
    } else {
      window.XNNiche = { key: '', source: 'parent' };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
